import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  ShieldCheck,
} from '@phosphor-icons/react';
import { STEPS } from './agency-signup/constants';
import { useAgencySignup } from './agency-signup/useAgencySignup';
import SignupProgress from './agency-signup/SignupProgress';
import {
  AgencyInfoStep,
  SandataStep,
  AddressContactStep,
  AdminAccountStep,
} from './agency-signup/SignupSteps';
import SignupReviewStep from './agency-signup/SignupReviewStep';
import SignupUnavailable from '../components/auth/SignupUnavailable';
import { useSignupPolicy } from '../hooks/useSignupPolicy';

const STEP_DESCRIPTIONS = {
  1: 'Enter your agency details as registered with Ohio Medicaid',
  2: 'Sandata/HHAExchange EVV configuration (optional — can be added later)',
  3: 'Agency physical address and primary contact',
  4: 'Create your administrator account',
  5: 'Review your information before submitting',
};

const AgencySignupPage = () => {
  const { enabled: signupEnabled, loading: policyLoading } = useSignupPolicy();
  const {
    step,
    setStep,
    form,
    errors,
    submitting,
    set,
    setField,
    nextStep,
    prevStep,
    handleSubmit,
  } = useAgencySignup();

  const renderStep = () => {
    switch (step) {
      case 1:
        return <AgencyInfoStep form={form} set={set} setField={setField} errors={errors} />;
      case 2:
        return <SandataStep form={form} set={set} />;
      case 3:
        return <AddressContactStep form={form} set={set} errors={errors} />;
      case 4:
        return <AdminAccountStep form={form} set={set} errors={errors} />;
      case 5:
        return <SignupReviewStep form={form} />;
      default:
        return null;
    }
  };

  if (!policyLoading && !signupEnabled) {
    return <SignupUnavailable />;
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4"
      data-testid="agency-signup-page"
    >
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-3">
            <ShieldCheck size={32} weight="duotone" className="text-indigo-900" />
            <h1
              className="text-2xl font-bold text-indigo-900"
              style={{ fontFamily: 'Manrope' }}
            >
              AZAI Billing
            </h1>
          </div>
          <p className="text-slate-500 text-sm">Register your home health agency</p>
        </div>

        <SignupProgress step={step} setStep={setStep} />

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              {STEPS[step - 1].title}
            </CardTitle>
            <CardDescription>{STEP_DESCRIPTIONS[step]}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {renderStep()}

            {/* Navigation */}
            <div className="flex justify-between pt-4 border-t">
              {step > 1 ? (
                <Button variant="outline" onClick={prevStep} data-testid="btn-prev-step">
                  <ArrowLeft size={16} className="mr-2" /> Back
                </Button>
              ) : (
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="text-slate-500"
                    data-testid="btn-back-to-login"
                  >
                    <ArrowLeft size={16} className="mr-2" /> Back to Login
                  </Button>
                </Link>
              )}
              {step < 5 ? (
                <Button
                  onClick={nextStep}
                  className="bg-indigo-900 hover:bg-indigo-800"
                  data-testid="btn-next-step"
                >
                  Next <ArrowRight size={16} className="ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-indigo-900 hover:bg-indigo-800"
                  data-testid="btn-submit-signup"
                >
                  {submitting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <CheckCircle size={16} className="mr-2" />
                  )}
                  Register Agency
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-4">
          Already have an account?{' '}
          <Link to="/login" className="text-indigo-600 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AgencySignupPage;
